import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';

import { brand, destructive } from 'utils/designTokens';

const heroActionThemes = {
  start: {
    bgcolor: 'rgba(255,255,255,0.16)',
    borderColor: 'rgba(255, 213, 79, 0.72)',
    boxShadow: '0 6px 22px rgba(255, 213, 79, 0.28), inset 0 1px 0 rgba(255,255,255,0.22)',
    iconBg: '#ffd54f',
    iconColor: brand.primaryDark,
    hoverBg: 'rgba(255,255,255,0.24)',
    hoverBorder: '#ffd54f',
    hoverShadow: '0 8px 28px rgba(255, 213, 79, 0.42), inset 0 1px 0 rgba(255,255,255,0.28)'
  },
  close: {
    bgcolor: 'rgba(183, 28, 28, 0.42)',
    borderColor: 'rgba(255, 183, 178, 0.75)',
    boxShadow: '0 6px 22px rgba(183, 28, 28, 0.38), inset 0 1px 0 rgba(255,255,255,0.14)',
    iconBg: '#fff',
    iconColor: destructive.main,
    hoverBg: 'rgba(183, 28, 28, 0.55)',
    hoverBorder: '#ffcdd2',
    hoverShadow: '0 8px 26px rgba(183, 28, 28, 0.48), inset 0 1px 0 rgba(255,255,255,0.18)'
  },
  restart: {
    bgcolor: 'rgba(255, 167, 38, 0.3)',
    borderColor: 'rgba(255, 204, 128, 0.72)',
    boxShadow: '0 6px 20px rgba(255, 167, 38, 0.32), inset 0 1px 0 rgba(255,255,255,0.16)',
    iconBg: '#ffe082',
    iconColor: '#e65100',
    hoverBg: 'rgba(255, 167, 38, 0.42)',
    hoverBorder: '#ffcc80',
    hoverShadow: '0 8px 26px rgba(255, 167, 38, 0.45), inset 0 1px 0 rgba(255,255,255,0.2)'
  }
};

function HeroIconBadge({ loading, Icon, theme }) {
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: theme.iconBg,
        color: theme.iconColor,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
        '& svg': { fontSize: 16 }
      }}
    >
      {loading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <Icon />}
    </Box>
  );
}

HeroIconBadge.propTypes = {
  loading: PropTypes.bool,
  Icon: PropTypes.elementType,
  theme: PropTypes.object
};

function ActionButton({ variant, action, label, icon: Icon, disabled, acting, onClick, isClose }) {
  const loading = acting === action;
  const heroTheme = heroActionThemes[action] ?? heroActionThemes.start;

  if (variant === 'hero') {
    return (
      <Button
        variant="contained"
        size="small"
        disableElevation
        disabled={disabled || Boolean(acting)}
        startIcon={<HeroIconBadge loading={loading} Icon={Icon} theme={heroTheme} />}
        onClick={onClick}
        sx={{
          bgcolor: heroTheme.bgcolor,
          color: '#fff',
          fontWeight: 700,
          textTransform: 'none',
          borderRadius: '999px',
          px: 2.25,
          py: 0.85,
          border: '2px solid',
          borderColor: heroTheme.borderColor,
          boxShadow: heroTheme.boxShadow,
          backdropFilter: 'blur(8px)',
          whiteSpace: 'nowrap',
          transition: 'all 0.22s ease',
          '&:hover': {
            bgcolor: heroTheme.hoverBg,
            borderColor: heroTheme.hoverBorder,
            boxShadow: heroTheme.hoverShadow,
            transform: 'translateY(-1px)'
          },
          '&.Mui-disabled': {
            bgcolor: 'rgba(255,255,255,0.1)',
            borderColor: 'rgba(255,255,255,0.18)',
            color: 'rgba(255,255,255,0.42)',
            transform: 'none'
          },
          '& .MuiButton-startIcon': { ml: 0.25, mr: 0.85 }
        }}
      >
        {label}
      </Button>
    );
  }

  if (isClose) {
    return (
      <Button
        variant="contained"
        size="small"
        disabled={disabled || Boolean(acting)}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Icon />}
        onClick={onClick}
        sx={{
          borderRadius: 2,
          fontWeight: 600,
          textTransform: 'none',
          bgcolor: destructive.main,
          boxShadow: destructive.shadow,
          '&:hover': { bgcolor: destructive.dark }
        }}
      >
        {label}
      </Button>
    );
  }

  return (
    <Button
      variant="contained"
      color="primary"
      size="small"
      disabled={disabled || Boolean(acting)}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Icon />}
      onClick={onClick}
      sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
    >
      {label}
    </Button>
  );
}

ActionButton.propTypes = {
  variant: PropTypes.string,
  action: PropTypes.string,
  label: PropTypes.string,
  icon: PropTypes.elementType,
  disabled: PropTypes.bool,
  acting: PropTypes.string,
  onClick: PropTypes.func,
  isClose: PropTypes.bool
};

export default function DayOpeningActions({
  variant = 'page',
  showAll = false,
  acting,
  canStart,
  canClose,
  canRestart,
  onStart,
  onClose,
  onRestart,
  confirmAction,
  confirmCopy,
  onConfirmClose,
  onConfirm,
  onCancelConfirm
}) {
  const buttons = showAll ? (
    <>
      <ActionButton
        variant={variant}
        action="start"
        label="Start day"
        icon={PlayArrowRoundedIcon}
        disabled={!canStart}
        acting={acting}
        onClick={onStart}
      />
      <ActionButton
        variant={variant}
        action="close"
        label="Close day"
        icon={StopRoundedIcon}
        disabled={!canClose}
        acting={acting}
        onClick={onClose}
        isClose
      />
      <ActionButton
        variant={variant}
        action="restart"
        label="Restart day"
        icon={RestartAltRoundedIcon}
        disabled={!canRestart}
        acting={acting}
        onClick={onRestart}
      />
    </>
  ) : (
    <>
      {canClose ? (
        <ActionButton
          variant={variant}
          action="close"
          label="Close day"
          icon={StopRoundedIcon}
          acting={acting}
          onClick={onClose}
          isClose
        />
      ) : canRestart ? (
        <ActionButton
          variant={variant}
          action="restart"
          label="Restart day"
          icon={RestartAltRoundedIcon}
          acting={acting}
          onClick={onRestart}
        />
      ) : (
        <ActionButton
          variant={variant}
          action="start"
          label="Start day"
          icon={PlayArrowRoundedIcon}
          acting={acting}
          onClick={onStart}
        />
      )}
    </>
  );

  return (
    <>
      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
        {buttons}
      </Stack>

      <Dialog
        open={Boolean(confirmAction)}
        onClose={onConfirmClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmCopy?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmCopy?.body}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onCancelConfirm}
            disabled={Boolean(acting)}
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={Boolean(acting)}
            onClick={onConfirm}
            startIcon={acting ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              ...(confirmAction === 'close'
                ? { bgcolor: destructive.main, '&:hover': { bgcolor: destructive.dark } }
                : {})
            }}
          >
            {acting ? 'Please wait…' : confirmCopy?.label}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

DayOpeningActions.propTypes = {
  variant: PropTypes.oneOf(['page', 'hero']),
  showAll: PropTypes.bool,
  acting: PropTypes.string,
  canStart: PropTypes.bool,
  canClose: PropTypes.bool,
  canRestart: PropTypes.bool,
  onStart: PropTypes.func,
  onClose: PropTypes.func,
  onRestart: PropTypes.func,
  confirmAction: PropTypes.string,
  confirmCopy: PropTypes.object,
  onConfirmClose: PropTypes.func,
  onConfirm: PropTypes.func,
  onCancelConfirm: PropTypes.func
};
